import { Link, useNavigate, useParams } from 'react-router';
import {
  IconArrowLeft,
  IconClipboardCheck,
  IconClock,
  IconLock,
  IconPhoto,
  IconShieldCheck,
} from '@tabler/icons-react';
import {
  Badge,
  Button,
  EmptyState,
  Metric,
  Panel,
  Progress,
  cn,
  formatDate,
  formatMoney,
  formatPercentage,
  type BadgeTone,
  type SchedulePhase,
} from 'escrowbuild-ui';

import {
  useFixtureVocabulary,
  useMilestones,
  usePublishedDocuments,
  usePublishedEvidence,
  usePublishedPublications,
  useScheduleOf,
  useT,
  type AppTranslationKey,
} from '@/i18n';
import {
  findProject,
  participationsForInvestor,
  projectTotals,
  type DocumentRecord,
} from '@/data/domain';

/* =============================================================================
   Investor portal — one development
   -----------------------------------------------------------------------------
   The screen an investor opens most often, and the one where the platform's
   permission model is most visible. Three rules hold across it:

     1. A development outside the visitor's participations is refused whole. The
        project record is not resolved at all, so there is no name, phase or
        figure in the render for the refusal to leak.
     2. Other investors' positions are never shown. The ownership panel carries
        the structure and the visitor's own line, and says why the rest is absent.
     3. Financial figures carry their provenance. The approved summary is the
        figure a published update disclosed, and it names the update.
   ============================================================================= */

/** The signed-in investor — see the note in `PortalHomePage`. */
const INVESTOR_ID = 'prado';

const CARD = 'rounded-xs border border-line bg-surface shadow-hard-sm';
const FOCUS_RING =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus';

/** The word each document kind is shown as. The code itself is a wire value. */
const DOCUMENT_KIND: Record<DocumentRecord['type'], AppTranslationKey> = {
  invoice: 'status.documentType.invoice',
  receipt: 'status.documentType.receipt',
  contract: 'status.documentType.contract',
  permit: 'status.documentType.permit',
  quote: 'status.documentType.quote',
  report: 'status.documentType.report',
  inspection: 'status.documentType.inspection',
};

const PHASE_TONES: Record<SchedulePhase['status'], BadgeTone> = {
  completed: 'success',
  'in-progress': 'info',
  planned: 'neutral',
};

const PHASE_LABELS: Record<SchedulePhase['status'], AppTranslationKey> = {
  completed: 'status.phase.completed',
  'in-progress': 'status.phase.inProgress',
  planned: 'status.phase.planned',
};

/** Newest first, over a COPY: the caller's list is a fresh one, so the sort
 *  cannot reach a memoised array another reader of it is holding. */
function newestFirst<T extends { publishedAt: string | null }>(rows: T[]): T[] {
  return rows.sort((a, b) => (b.publishedAt ?? '').localeCompare(a.publishedAt ?? ''));
}

/**
 * One phase of the schedule.
 *
 * The bars are positioned as percentages of the schedule window and carry no
 * text of their own, so the row states the phase's own figures beside them: an
 * overlap with the phase above is what shows the phases run concurrently, and
 * the line underneath is how the row is read without seeing the picture.
 */
function ScheduleRow({ phase }: { phase: SchedulePhase }) {
  const t = useT();

  return (
    <li className="flex flex-col gap-2 border-b border-line-subtle py-3 last:border-b-0 sm:flex-row sm:items-center sm:gap-4">
      <div className="flex items-center justify-between gap-3 sm:w-80 sm:shrink-0 sm:justify-start">
        <span className="min-w-0 truncate text-sm text-body">{phase.name}</span>
        <Badge tone={PHASE_TONES[phase.status]} variant="subtle" size="sm">
          {t(PHASE_LABELS[phase.status])}
        </Badge>
      </div>

      <div className="min-w-0 flex-1">
        <div aria-hidden="true" className="relative h-6 w-full rounded-xs bg-surface-muted">
          <span
            className="absolute top-1 h-1.5 rounded-xs bg-line-strong"
            style={{ left: `${phase.plannedStart}%`, width: `${phase.plannedWidth}%` }}
          />
          {phase.actualStart !== null && phase.actualWidth !== null ? (
            <span
              className={cn(
                'absolute bottom-1 h-1.5 rounded-xs',
                phase.status === 'completed' ? 'bg-success' : 'bg-primary',
              )}
              style={{ left: `${phase.actualStart}%`, width: `${phase.actualWidth}%` }}
            />
          ) : null}
        </div>
        <p className="mt-1.5 text-xs text-muted">
          {t('portal.project.budgeted', { amount: formatMoney(phase.budgeted) })} ·{' '}
          {phase.actual > 0
            ? t('portal.project.actual', { amount: formatMoney(phase.actual) })
            : t('portal.project.noSpend')}
        </p>
      </div>
    </li>
  );
}

export function PortalProjectPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const t = useT();

  const participation = participationsForInvestor(INVESTOR_ID).find(
    (entry) => entry.projectId === projectId,
  );
  // Resolved only once a participation exists, so a development the visitor
  // holds nothing in is never read out of the project list at all.
  const project = participation ? findProject(projectId) : undefined;

  /*
     A phase's name, a milestone's title, the approved update and the released
     record, all resolved for the active locale. They are resolved BEFORE the
     refusal below, because a hook cannot run after a conditional return — the
     empty development selects nothing, and that branch renders no list at all.
  */
  const id = project?.id ?? '';
  const phases = useScheduleOf(id);
  const milestones = useMilestones(id);
  const releases = usePublishedPublications(id);
  /* The phase each record names is the one word on this screen that is not
     reached through the schedule. */
  const vocabulary = useFixtureVocabulary();
  const released = usePublishedDocuments(id);
  const photos = usePublishedEvidence(id);

  /** A phase's name, or the sentence this screen uses for one it does not hold. */
  const phaseLabel = (phaseId: string | null, fallback: string) =>
    phases.find((phase) => phase.id === phaseId)?.name ?? fallback;

  if (!participation || !project) {
    return (
      <section className="flex flex-col">
        <h1 className="sr-only">{t('portal.project.notInPortfolio')}</h1>
        <EmptyState
          variant="page"
          icon={<IconLock size={32} aria-hidden="true" />}
          title={t('portal.project.refusedTitle')}
          description={t('portal.project.refusedBody')}
          action={
            <Button onClick={() => navigate('/portal')}>{t('portal.project.backToPortfolio')}</Button>
          }
        />
      </section>
    );
  }

  const totals = projectTotals(project.id);

  /*
     Reached milestones are filtered by `notified` — the flag that records an
     investor was actually told. §11's rule is about disclosure rather than about
     which table a row lives in, so a milestone nobody was notified about stays
     internal work.
  */
  const reached = milestones
    .filter((milestone) => milestone.status === 'reached' && milestone.notified)
    .sort((a, b) => b.date.localeCompare(a.date));
  const upcoming = milestones
    .filter((milestone) => milestone.status === 'upcoming')
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 4);

  const approved = newestFirst(releases.filter((entry) => entry.projectId === project.id))[0];
  const documents = released.filter((entry) => entry.projectId === project.id);
  const evidence = photos.filter((entry) => entry.projectId === project.id);

  return (
    <div className="flex flex-col gap-8">
      <div>
        <Link
          to="/portal"
          className={cn(
            'inline-flex items-center gap-1.5 rounded-xs text-sm font-medium text-muted no-underline hover:text-heading',
            FOCUS_RING,
          )}
        >
          <IconArrowLeft size={14} aria-hidden="true" />
          {t('portal.project.backLink')}
        </Link>
      </div>

      <header className="flex flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-display text-3xl font-semibold uppercase tracking-title text-heading">
              {project.name}
            </h1>
            <p className="mt-1 text-sm text-muted">
              {project.address} · {project.city} · {project.lot}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="neutral" variant="subtle" size="sm">
              {vocabulary.phase(project.phase)}
            </Badge>
            <Badge tone="info" variant="subtle" size="sm">
              {t('portal.project.share', {
                share: formatPercentage(participation.sharePercent, { precision: 0 }),
              })}
            </Badge>
          </div>
        </div>

        <div className="max-w-2xl">
          <Progress
            value={project.progress}
            label={t('portal.project.progress')}
            showValue
            size="sm"
            tone="primary"
          />
        </div>
      </header>

      <Panel
        title={t('portal.project.participationTitle')}
        description={t('portal.project.participatingSince', {
          date: formatDate(participation.joinedAt),
        })}
      >
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
          <Metric
            label={t('portal.project.yourShare')}
            value={participation.sharePercent}
            format="percentage"
            precision={0}
            hint={t('portal.project.yourShareHint')}
          />
          <Metric
            label={t('portal.project.committed')}
            value={participation.committed}
            format="money"
          />
          <Metric
            label={t('portal.project.contributed')}
            value={participation.contributed}
            format="money"
            hint={t('portal.project.contributedHint')}
          />
          <div className="min-w-0">
            <p className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
              {t('portal.project.capitalCalls')}
            </p>
            <p className="mt-1 text-3xl font-semibold leading-none text-heading">
              {participation.callsSettled}
              <span className="text-xl text-muted"> / {participation.callsIssued}</span>
            </p>
            <p className="mt-2 text-xs text-muted">{t('portal.project.callsSettledHint')}</p>
          </div>
        </div>
      </Panel>

      <Panel title={t('portal.project.ownershipTitle')}>
        <ul className="list-none divide-y divide-line-subtle p-0">
          <li className="flex items-baseline justify-between gap-4 py-3 first:pt-0">
            <span className="text-sm text-body">{t('portal.project.retainedByBuilder')}</span>
            <span className="text-sm font-medium text-heading">
              {formatPercentage(project.ownershipShare, { precision: 0 })}
            </span>
          </li>
          <li className="flex items-baseline justify-between gap-4 py-3">
            <span className="text-sm text-body">{t('portal.project.heldByInvestors')}</span>
            <span className="text-sm font-medium text-heading">
              {formatPercentage(100 - project.ownershipShare, { precision: 0 })}
            </span>
          </li>
          <li className="flex items-baseline justify-between gap-4 bg-surface-muted py-3">
            <span className="text-sm font-medium text-heading">
              {t('portal.project.yourParticipation')}
            </span>
            <span className="text-sm font-medium text-heading">
              {formatPercentage(participation.sharePercent, { precision: 0 })}
            </span>
          </li>
        </ul>
        <p className="mt-4 text-xs text-muted">{t('portal.project.ownershipNote')}</p>
      </Panel>

      <Panel
        title={t('portal.project.scheduleTitle')}
        description={t('portal.project.scheduleDescription')}
      >
        <div aria-hidden="true" className="hidden gap-4 pb-2 sm:flex">
          <span className="sm:w-80 sm:shrink-0" />
          <div className="relative min-w-0 flex-1 pt-4">
            {[0, 25, 50, 75, 100].map((tick) => (
              <span
                key={tick}
                className={cn(
                  'absolute top-0 text-2xs text-muted',
                  tick === 0
                    ? 'translate-x-0'
                    : tick === 100
                      ? '-translate-x-full'
                      : '-translate-x-1/2',
                )}
                style={{ left: `${tick}%` }}
              >
                {tick}%
              </span>
            ))}
            <span className="block h-px w-full bg-line-subtle" />
          </div>
        </div>

        <ul className="list-none p-0">
          {phases.map((phase) => (
            <ScheduleRow key={phase.id} phase={phase} />
          ))}
        </ul>

        <div className="mt-5 flex flex-wrap items-center gap-4 text-xs text-muted">
          <span className="inline-flex items-center gap-2">
            <span aria-hidden="true" className="h-1.5 w-6 rounded-xs bg-line-strong" />
            {t('status.phase.planned')}
          </span>
          <span className="inline-flex items-center gap-2">
            <span aria-hidden="true" className="h-1.5 w-6 rounded-xs bg-primary" />
            {t('portal.project.legendActualInProgress')}
          </span>
          <span className="inline-flex items-center gap-2">
            <span aria-hidden="true" className="h-1.5 w-6 rounded-xs bg-success" />
            {t('portal.project.legendActualCompleted')}
          </span>
        </div>
      </Panel>

      <Panel title={t('portal.project.milestonesTitle')}>
        <div className="grid gap-8 lg:grid-cols-2">
          <div>
            <h3 className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
              {t('portal.project.milestonesNext')}
            </h3>
            {upcoming.length === 0 ? (
              <p className="mt-3 text-sm text-muted">{t('portal.project.noUpcomingMilestones')}</p>
            ) : (
              <ul className="mt-3 list-none divide-y divide-line-subtle p-0">
                {upcoming.map((milestone) => (
                  <li key={milestone.id} className="flex items-start gap-3 py-3 first:pt-0">
                    <IconClock size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-muted" />
                    <div className="min-w-0">
                      <p className="text-sm text-body">{milestone.title}</p>
                      <p className="mt-0.5 text-xs text-muted">
                        {phaseLabel(milestone.phaseId, t('portal.project.phaseFallback'))} ·{' '}
                        {formatDate(milestone.date)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <h3 className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
              {t('portal.project.milestonesReached')}
            </h3>
            {reached.length === 0 ? (
              <p className="mt-3 text-sm text-muted">{t('portal.project.noReachedMilestones')}</p>
            ) : (
              <ul className="mt-3 list-none divide-y divide-line-subtle p-0">
                {reached.map((milestone) => (
                  <li key={milestone.id} className="flex items-start gap-3 py-3 first:pt-0">
                    <IconClipboardCheck
                      size={16}
                      aria-hidden="true"
                      className="mt-0.5 shrink-0 text-success"
                    />
                    <div className="min-w-0">
                      <p className="text-sm text-body">{milestone.title}</p>
                      <p className="mt-0.5 text-xs text-muted">{formatDate(milestone.date)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Panel>

      <Panel
        title={t('portal.project.approvedTitle')}
        description={t('portal.project.approvedDescription')}
      >
        {approved ? (
          <div className="flex flex-col gap-5">
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              <Metric
                label={t('portal.project.spendToDate')}
                value={approved.spendToDate}
                format="money"
                hint={t('portal.project.spendToDateHint')}
              />
              <Metric
                label={t('portal.project.againstForecast')}
                value={approved.variance}
                format="money"
                tone={approved.variance < 0 ? 'success' : 'warning'}
                hint={t('portal.project.againstForecastHint')}
              />
              <Metric
                label={t('portal.project.yourShareUnspent')}
                value={(totals.available * participation.sharePercent) / 100}
                format="money"
                hint={t('portal.project.yourShareUnspentHint')}
              />
            </div>

            <div className="flex items-start gap-3 rounded-xs border border-line-subtle bg-surface-muted p-4">
              <IconShieldCheck
                size={16}
                aria-hidden="true"
                className="mt-0.5 shrink-0 text-primary"
              />
              <p className="text-sm text-body">
                {t('portal.project.disclosedIn')}{' '}
                <span className="font-medium text-heading">{approved.title}</span>
                {t('portal.project.disclosedRest', {
                  period: approved.period,
                  approvedBy: approved.approvedBy ?? '—',
                  approvedAt: formatDate(approved.approvedAt),
                  publishedAt: formatDate(approved.publishedAt),
                })}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted">{t('portal.project.noApprovedYet')}</p>
        )}
      </Panel>

      <Panel
        title={t('portal.project.evidenceTitle')}
        description={t('portal.project.evidenceCount', { count: evidence.length })}
      >
        {evidence.length === 0 ? (
          <p className="text-sm text-muted">{t('portal.project.noEvidence')}</p>
        ) : (
          <ul className="grid list-none gap-4 p-0 sm:grid-cols-2 xl:grid-cols-3">
            {evidence.map((item) => (
              <li key={item.id} className={cn(CARD, 'flex flex-col gap-3 p-4')}>
                <div className="grid h-24 place-items-center rounded-xs bg-surface-muted">
                  <IconPhoto size={24} aria-hidden="true" className="text-muted" />
                </div>
                <p className="text-sm text-body">{item.caption}</p>
                <div className="mt-auto flex flex-wrap items-center gap-2">
                  <Badge tone="neutral" variant="subtle" size="sm">
                    {phaseLabel(item.phaseId, t('portal.project.phaseFallback'))}
                  </Badge>
                  <span className="text-xs text-muted">{formatDate(item.date)}</span>
                </div>
                <p className="text-xs text-muted">
                  {t('portal.project.capturedBy', { name: item.capturedBy })}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel
        title={t('portal.project.documentsTitle')}
        description={t('portal.project.documentsDescription')}
        headerActions={
          <Button variant="outline" size="sm" onClick={() => navigate('/portal/documents')}>
            {t('portal.project.openDocuments')}
          </Button>
        }
      >
        {documents.length === 0 ? (
          <p className="text-sm text-muted">{t('portal.project.noDocuments')}</p>
        ) : (
          <ul className="list-none divide-y divide-line-subtle p-0">
            {documents.map((document) => (
              <li
                key={document.id}
                className="flex items-baseline justify-between gap-4 py-3 first:pt-0"
              >
                <span className="min-w-0 text-sm text-body">{document.name}</span>
                <span className="shrink-0 text-xs text-muted">
                  {t(DOCUMENT_KIND[document.type])} · {formatDate(document.date)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
